import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Bot, 
  Calendar, 
  Sparkles, 
  X, 
  Pause, 
  Play,
  MoreVertical,
  AlertCircle,
  Check,
  Zap
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/Badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { cn } from '../../../utils/cn';
import { QueuedResponse } from '../hooks/useQueuedResponses';

interface QueuedResponseBubbleProps {
  queuedResponse: QueuedResponse;
  onCancel?: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onSendNow?: (id: string) => void;
  onReschedule?: (id: string, delayMinutes: number) => void;
}

interface TimeRemaining {
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
}

export const QueuedResponseBubble: React.FC<QueuedResponseBubbleProps> = ({
  queuedResponse,
  onCancel,
  onPause,
  onResume,
  onSendNow,
  onReschedule
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0
  });
  const [showFullResponse, setShowFullResponse] = useState(false);

  // Debug logging to understand why Send Now button isn't showing
  useEffect(() => {
    console.log('🚀 QueuedResponseBubble Debug:', {
      queuedResponseId: queuedResponse.id,
      executionStatus: queuedResponse.executionStatus,
      hasOnSendNow: !!onSendNow,
      timeRemainingSeconds: timeRemaining.totalSeconds,
      timeRemainingObject: timeRemaining,
      scheduledFor: queuedResponse.scheduledFor,
      showButtonCondition: !!onSendNow && timeRemaining.totalSeconds > 0,
      queuedResponseKeys: Object.keys(queuedResponse),
      content: queuedResponse.generatedResponse ? `${queuedResponse.generatedResponse.substring(0, 50)}...` : 'No content'
    });
  }, [queuedResponse, onSendNow, timeRemaining]);

  // Calculate time remaining
  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = new Date().getTime();
      const scheduledTime = new Date(queuedResponse.scheduledFor).getTime();
      const diff = Math.max(0, scheduledTime - now);

      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setTimeRemaining({ hours, minutes, seconds, totalSeconds });
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [queuedResponse.scheduledFor]);

  const formatCountdown = () => {
    if (timeRemaining.totalSeconds <= 0) {
      return 'Sending now...';
    }

    if (timeRemaining.hours > 0) {
      return `${timeRemaining.hours}h ${timeRemaining.minutes}m ${timeRemaining.seconds}s`;
    } else if (timeRemaining.minutes > 0) {
      return `${timeRemaining.minutes}m ${timeRemaining.seconds}s`;
    } else {
      return `${timeRemaining.seconds}s`;
    }
  };

  const getStatusColor = () => {
    if (timeRemaining.totalSeconds <= 0) return 'text-green-600 dark:text-green-400';
    if (timeRemaining.totalSeconds <= 60) return 'text-orange-600 dark:text-orange-400';
    return 'text-blue-600 dark:text-blue-400';
  };

  const getStatusBadgeColor = () => {
    if (timeRemaining.totalSeconds <= 0) return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
    if (timeRemaining.totalSeconds <= 60) return 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800';
    return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800';
  };

  const truncatedResponse = queuedResponse.finalResponse.length > 200 
    ? queuedResponse.finalResponse.substring(0, 200) + '...'
    : queuedResponse.finalResponse;

  const scheduledTime = new Date(queuedResponse.scheduledFor);
  const isUrgent = timeRemaining.totalSeconds <= 60 && timeRemaining.totalSeconds > 0;

  return (
    <TooltipProvider>
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="flex justify-end mb-4"
      >
        <div className="max-w-[80%] space-y-2">
          {/* Status Header */}
          <div className="flex items-center justify-end gap-2 text-sm">
            <Badge className={cn("border text-xs font-medium", getStatusBadgeColor())}>
              <Bot className="h-3 w-3 mr-1" />
              {timeRemaining.totalSeconds <= 0 ? 'Sending...' : 'Queued Response'}
            </Badge>
            <div className={cn("flex items-center gap-1 font-mono text-xs", getStatusColor())}>
              <Clock className={cn("h-3 w-3", isUrgent && "animate-pulse")} />
              <span className={isUrgent ? "animate-pulse font-semibold" : ""}>
                {formatCountdown()}
              </span>
            </div>
          </div>

          {/* Response Bubble */}
          <div className={cn(
            "relative bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-2xl rounded-tr-sm p-4 shadow-lg",
            "border-2 border-blue-400/30 backdrop-blur-sm",
            isUrgent && "animate-pulse border-orange-400/50"
          )}>
            {/* Automation Indicator */}
            <div className="absolute -top-2 -right-2">
              <div className={cn(
                "h-6 w-6 rounded-full bg-white dark:bg-gray-800 border-2 border-blue-500 flex items-center justify-center",
                isUrgent && "border-orange-500"
              )}>
                <Sparkles className="h-3 w-3 text-blue-500" />
              </div>
            </div>

            {/* Message Content */}
            <div className="space-y-3">
              <div className="text-white/90 text-sm leading-relaxed">
                {showFullResponse ? queuedResponse.finalResponse : truncatedResponse}
                {queuedResponse.finalResponse.length > 200 && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowFullResponse(!showFullResponse)}
                    className="text-white/80 hover:text-white underline p-0 h-auto ml-1"
                  >
                    {showFullResponse ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </div>

              {/* Metadata */}
              <div className="flex items-center justify-between text-xs text-white/70">
                <div className="flex items-center gap-3">
                  <Tooltip>
                    <TooltipTrigger>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{scheduledTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="text-xs">
                        <div><strong>Scheduled for:</strong></div>
                        <div>{scheduledTime.toLocaleString()}</div>
                        <div className="mt-1"><strong>Delay:</strong> {queuedResponse.scheduledDelayMinutes} minutes</div>
                      </div>
                    </TooltipContent>
                  </Tooltip>

                  {queuedResponse.metadata?.confidence && (
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          <span>{Math.round(queuedResponse.metadata.confidence * 100)}%</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-xs">
                          <div><strong>AI Confidence:</strong> {Math.round(queuedResponse.metadata.confidence * 100)}%</div>
                          {queuedResponse.metadata.modelUsed && (
                            <div><strong>Model:</strong> {queuedResponse.metadata.modelUsed}</div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  <Badge className="bg-white/20 text-white border-white/30 text-xs">
                    Automated
                  </Badge>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1">
                  {/* Send Now Button - Enhanced & More Aggressive */}
                  {onSendNow && timeRemaining.totalSeconds > 0 && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onSendNow(queuedResponse.id)}
                          className={cn(
                            "h-8 w-8 text-white hover:text-white transition-all duration-200 border-2",
                            timeRemaining.totalSeconds > 60 
                              ? "bg-green-500/60 hover:bg-green-500/80 border-green-400/70 shadow-lg" 
                              : "bg-green-500/40 hover:bg-green-500/60 border-green-400/50 shadow-sm"
                          )}
                        >
                          <Play className={cn(
                            "h-4 w-4",
                            timeRemaining.totalSeconds > 60 && "drop-shadow-sm"
                          )} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-green-600 border-green-500">
                        <div className="text-xs text-white">
                          <div className="font-semibold">⚡ Send Now</div>
                          <div className="opacity-90">Skip {formatCountdown()} wait</div>
                          <div className="text-xs opacity-75">Status: {queuedResponse.executionStatus || 'unknown'}</div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {/* Pause Button */}
                  {queuedResponse.executionStatus === 'pending' && onPause && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onPause(queuedResponse.id)}
                          className="h-6 w-6 text-white/70 hover:text-white hover:bg-white/20"
                        >
                          <Pause className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Pause countdown</TooltipContent>
                    </Tooltip>
                  )}

                  {/* Resume Button (for paused responses) */}
                  {queuedResponse.executionStatus === 'paused' && onResume && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onResume(queuedResponse.id)}
                          className="h-6 w-6 text-white/70 hover:text-white hover:bg-white/20"
                        >
                          <Play className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Resume countdown</TooltipContent>
                    </Tooltip>
                  )}

                  {/* Reschedule Dropdown */}
                  {onReschedule && queuedResponse.executionStatus === 'pending' && timeRemaining.totalSeconds > 10 && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            // Simple reschedule options - could be enhanced with a modal
                            const options = [
                              { label: '5 minutes', value: 5 },
                              { label: '15 minutes', value: 15 },
                              { label: '30 minutes', value: 30 },
                              { label: '1 hour', value: 60 }
                            ];
                            
                            // For now, just reschedule to 15 minutes
                            // In a real implementation, show a dropdown or modal
                            onReschedule(queuedResponse.id, 15);
                          }}
                          className="h-6 w-6 text-white/70 hover:text-white hover:bg-white/20"
                        >
                          <Calendar className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Reschedule (defaults to 15 min)</TooltipContent>
                    </Tooltip>
                  )}

                  {/* Cancel Button */}
                  {onCancel && timeRemaining.totalSeconds > 10 && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onCancel(queuedResponse.id)}
                          className="h-6 w-6 text-white/70 hover:text-white hover:bg-red-500/30"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Cancel scheduled response</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Goal/Instructions Info */}
          {queuedResponse.automationRule.goal && (
            <div className="text-right">
              <Tooltip>
                <TooltipTrigger>
                  <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                    Goal: {queuedResponse.automationRule.goal.length > 50 
                      ? queuedResponse.automationRule.goal.substring(0, 50) + '...'
                      : queuedResponse.automationRule.goal
                    }
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="text-xs space-y-1">
                    <div><strong>Automation Goal:</strong></div>
                    <div>{queuedResponse.automationRule.goal}</div>
                    {queuedResponse.automationRule.instructions && (
                      <>
                        <div className="mt-2"><strong>Instructions:</strong></div>
                        <div>{queuedResponse.automationRule.instructions}</div>
                      </>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </motion.div>
    </TooltipProvider>
  );
}; 